import {Category as BoVioErrorCategory} from '../../api-types/bovio/response/error'
import {ErrorType as BoVioErrorTypes} from '../../api-types/bovio/response/error'
import {bookingErrorMessages} from '../messages'
import {ErrorCategory} from '../types/ErrorCategory'
import {
  BookingError,
  Psd2AuthErrorTypes,
  Psd2AuthStatusErrorType
} from '../types/ErrorResponse'

export const NETWORK_ERROR = 'Network Error'

/**
 * Array of error categories that are considered recoverable during booking creation.
 */
export const redirectOffersRecoverableErrorCategories: (
  | BoVioErrorCategory
  | ErrorCategory
)[] = [
  BoVioErrorCategory.CardNotSupported,
  BoVioErrorCategory.InsufficientFunds,
  BoVioErrorCategory.IncorrectCvc,
  BoVioErrorCategory.IncorrectCardNumber,
  BoVioErrorCategory.InvalidEmail,
  BoVioErrorCategory.GenericCardError,
  BoVioErrorCategory.Other,
  BoVioErrorCategory.GenericPaymentDeclined,
  BoVioErrorCategory.TokenizationServiceUnavailable,
  BoVioErrorCategory.PaymentLimitExceeded,
  BoVioErrorCategory.TechnicalError,
  BoVioErrorCategory.MisusedCard,
  BoVioErrorCategory.SecurityBlocks
]

/**
 * Checks if the provided booking error corresponds to a generic payment declined.
 * @param error - The optional booking error object that contains error details.
 *                The function specifically checks the `category` field in the `details` object
 *                to determine if it matches `ErrorCategory.GenericPaymentDeclined`.
 * @returns `true` if the error category is `GenericPaymentDeclined`; otherwise, `false`.
 */
export const getIsGenericPaymentDeclined = (error?: BookingError) => {
  return (
    error?.data?.details?.category === BoVioErrorCategory.GenericPaymentDeclined
  )
}

/**
 * Checks if the provided booking error corresponds to a generic fraud declined.
 * @param error - The optional booking error object that contains error details.
 *                The function specifically checks the `category` field in the `details` object
 *                to determine if it matches `ErrorCategory.GenericFraudDeclined`.
 * @returns `true` if the error category is `GenericFraudDeclined`; otherwise, `false`.
 */
export const getIsGenericFraudDeclined = (error?: BookingError) => {
  return (
    error?.data?.details?.category === BoVioErrorCategory.GenericFraudDeclined
  )
}

/**
 * Checks if the provided booking error corresponds to a generic card error.
 * @param error - The optional booking error object that contains error details.
 *                The function specifically checks the `category` field in the `details` object
 *                to determine if it matches `ErrorCategory.GenericCardError`.
 * @returns `true` if the error category is `GenericCardError`; otherwise, `false`.
 */
export const getIsGenericCardError = (error?: BookingError) => {
  return error?.data?.details?.category === BoVioErrorCategory.GenericCardError
}

/**
 * Checks if the provided booking error corresponds to a incorrect card number.
 * @param error - The optional booking error object that contains error details.
 *                The function specifically checks the `category` field in the `details` object
 *                to determine if it matches `ErrorCategory.IncorrectNumber`.
 * @returns `true` if the error category is `IncorrectNumber`; otherwise, `false`.
 */
export const getIsWrongCCError = (error?: BookingError) => {
  return (
    error?.data?.details?.category === BoVioErrorCategory.IncorrectCardNumber
  )
}

/**
 * Checks if the provided booking error corresponds to a incorrect CVC Number.
 * @param error - The optional booking error object that contains error details.
 *                The function specifically checks the `category` field in the `details` object
 *                to determine if it matches `ErrorCategory.IncorrectCvc`.
 * @returns `true` if the error category is `IncorrectCvc`; otherwise, `false`.
 */
export const getIsWrongCVCError = (error?: BookingError) => {
  return error?.data?.details?.category === BoVioErrorCategory.IncorrectCvc
}

/**
 * Checks if the provided booking error corresponds to a Brazilian tax ID invalid.
 * @param error - The optional booking error object that contains error details.
 *                The function specifically checks the `type` field in the `data` object
 *                to determine if it matches `BoVioErrorTypes.InputValidation` and the `errors` field in the `details` object
 *                to determine if it matches `ErrorCategory.BrazilianTaxIdInvalid`.
 * @returns `true` if the error type is `InputValidation` and the error field is `tax_registration_number`; otherwise, `false`.
 */
export const getIsTaxIdInvalid = (error?: BookingError) => {
  return (
    // Booking creation error
    (error?.data?.type === BoVioErrorTypes.InputValidation &&
      error?.data?.details?.errors?.some(
        error => error.field === 'payment.billing.taxId'
      )) ||
    // Booking status error
    (error?.data?.details?.category === BoVioErrorCategory.DataValidation &&
      error?.data?.details?.reason?.errors?.some(
        error => error.type === 'payments.tax_registration_number.invalid'
      ))
  )
}

/**
 * Checks if the provided booking error corresponds to a insufficient funds.
 * @param error - The optional booking error object that contains error details.
 *                The function specifically checks the `category` field in the `details` object
 *                to determine if it matches `ErrorCategory.InsufficientFunds`.
 * @returns `true` if the error category is `InsufficientFunds`; otherwise, `false`.
 */
export const getIsInsufficientFunds = (error?: BookingError) => {
  return error?.data?.details?.category === BoVioErrorCategory.InsufficientFunds
}

/**
 * Checks if the provided booking error corresponds to a card not supported.
 * @param error - The optional booking error object that contains error details.
 *                The function specifically checks the `category` field in the `details` object
 *                to determine if it matches `ErrorCategory.CardNotSupported`.
 * @returns `true` if the error category is `CardNotSupported`; otherwise, `false`.
 */
export const getIsCardNotSupported = (error?: BookingError) => {
  return error?.data?.details?.category === BoVioErrorCategory.CardNotSupported
}

/**
 * Checks if the provided booking error corresponds to a price mismatch.
 * @param error - The optional booking error object that contains error details.
 *                The function specifically checks the `category` field in the `details` object
 *                to determine if it matches `ErrorCategory.PriceMismatch`.
 * @returns `true` if the error category is `PriceMismatch`; otherwise, `false`.
 */
export const getIsPriceMismatch = (error?: BookingError) => {
  return error?.data?.details?.category === BoVioErrorCategory.PriceMismatch
}

/**
 * Checks if the provided booking error corresponds to a invalid email address.
 * @param error - The optional booking error object that contains error details.
 *                The function specifically checks the `category` field in the `details` object
 *                to determine if it matches `ErrorCategory.InvalidEmail`.
 * @returns `true` if the error category is `InvalidEmail`; otherwise, `false`.
 */
export const getIsInvalidEmail = (error?: BookingError) => {
  return error?.data?.details?.category === BoVioErrorCategory.InvalidEmail
}

/**
 * Determines whether the provided booking error represents a connection error.
 * @param error - The optional booking error object. The function checks if the `status`
 *                is `500` or if the `data` (cast as a string) matches the `NETWORK_ERROR` constant.
 * @returns `true` if the error indicates a connection error; otherwise, `false`.
 */
export const getIsConnectionError = (error?: BookingError) => {
  const dataStr = error?.data as unknown as string
  return (
    error?.status === 500 ||
    dataStr === NETWORK_ERROR ||
    dataStr?.includes?.('timeout') ||
    Boolean(error && error.status === undefined)
  )
}

/**
 * Checks if the provided booking error corresponds to a other category.
 * @param error - The optional booking error object that contains error details.
 *                The function specifically checks the `category` field in the `details` object
 *                to determine if it matches `ErrorCategory.Other`.
 * @returns `true` if the error category is `Other`; otherwise, `false`.
 */
export const getIsGenericSupplierOrBackendError = (error?: BookingError) => {
  return error?.data?.details?.category === BoVioErrorCategory.Other
}

/**
 * Checks if the provided booking error means the offer can no longer be booked.
 * An `offer_expired` error carries no category, so without the type check it
 * would fall through to a retryable network error.
 * @param error - The optional booking error object that contains error details.
 * @returns `true` if the error category is `SoldOut` or the type is `OfferExpired`; otherwise, `false`.
 */
export const getIsSoldOutError = (error?: BookingError) => {
  return (
    error?.data?.details?.category === BoVioErrorCategory.SoldOut ||
    error?.data?.type === BoVioErrorTypes.OfferExpired
  )
}

/**
 * Checks if the provided booking error indicates that the customer's payment was already
 * captured even though the booking could not be created.
 *
 * This scenario is specific to the iDEAL payment method.
 * If the booking creation fails (for example, the offer sells out while the user is
 * completing the iDEAL authorization), the payment has already left the customer's account
 * and a refund is owed.
 * @param error - The optional booking error object that contains error details.
 *                The function checks the `paymentAlreadyCaptured` flag inside
 *                `data.details.reason`.
 * @returns `true` if the payment was captured without a booking being created; otherwise, `false`.
 */
export const getIsPaymentAlreadyCaptured = (error?: BookingError) => {
  return Boolean(error?.data?.details?.reason?.paymentAlreadyCaptured)
}

/**
 * Checks if the provided booking error corresponds to a duplicate booking.
 * @param error - The optional booking error object that contains error details.
 *                The function specifically checks the `category` field in the `details` object
 *                to determine if it matches `ErrorCategory.DuplicateBooking`.
 * @returns `true` if the error category is `DuplicateBooking`; otherwise, `false`.
 */
export const getIsDuplicateBooking = (error?: BookingError) => {
  return error?.data?.details?.category === BoVioErrorCategory.DuplicateBooking
}

/**
 * Checks if the provided booking error corresponds to tokenization service unavailability.
 * @param error - The optional booking error object that contains error details.
 *                The function specifically checks the `category` field in the `details` object
 *                to determine if it matches `ErrorCategory.TokenizationServiceUnavailable`.
 * @returns `true` if the error category is `TokenizationServiceUnavailable`; otherwise, `false`.
 */
export const getIsTokenizationServiceUnavailable = (error?: BookingError) => {
  return (
    error?.data?.details?.category ===
    BoVioErrorCategory.TokenizationServiceUnavailable
  )
}

/**
 * Checks if the provided booking error corresponds to payment limit exceeded.
 * @param error - The optional booking error object that contains error details.
 * @returns `true` if the error category is `PaymentLimitExceeded`; otherwise, `false`.
 */
export const getIsPaymentLimitExceeded = (error?: BookingError) => {
  return (
    error?.data?.details?.category === BoVioErrorCategory.PaymentLimitExceeded
  )
}

/**
 * Checks if the provided booking error corresponds to a technical issue.
 * @param error - The optional booking error object that contains error details.
 * @returns `true` if the error category is `TechnicalIssue`; otherwise, `false`.
 */
export const getIsTechnicalIssue = (error?: BookingError) => {
  return error?.data?.details?.category === BoVioErrorCategory.TechnicalError
}

/**
 * Checks if the provided booking error corresponds to a misused card.
 * @param error - The optional booking error object that contains error details.
 * @returns `true` if the error category is `MisusedCard`; otherwise, `false`.
 */
export const getIsMisusedCard = (error?: BookingError) => {
  return error?.data?.details?.category === BoVioErrorCategory.MisusedCard
}

/**
 * Checks if the provided booking error corresponds to security blocks.
 * @param error - The optional booking error object that contains error details.
 * @returns `true` if the error category is `SecurityBlocks`; otherwise, `false`.
 */
export const getIsSecurityBlocks = (error?: BookingError) => {
  return error?.data?.details?.category === BoVioErrorCategory.SecurityBlocks
}

/**
 * Maps a given booking error to its corresponding error category, based on specific error checks.
 * @param error - The booking error object (optional). This object contains details that determine
 *                the type of error encountered. Specific error checks such as
 *                `getIsGenericPaymentDeclined` or `getIsCardNotSupported` are used
 *                to identify the error category.
 * @returns The mapped error category from `BoVioErrorCategory` or `ErrorCategory`,
 *          a network error constant (`NETWORK_ERROR`), or `undefined` if no matching category is found.
 * @example
 * ```typescript
 * const error = {
 *   data: {
 *     details: {
 *       category: BoVioErrorCategory.GenericFraudDeclined
 *     }
 *   }
 * };
 * const result = getMappedBookingErrorToErrorCategory(error);
 * // Expected output: BoVioErrorCategory.GenericFraudDeclined
 * const noError = undefined;
 * const result = getMappedBookingErrorToErrorCategory(noError);
 * // Expected output: undefined
 * ```
 */
export const getMappedBookingErrorToErrorCategory = (error?: BookingError) => {
  if (getIsGenericPaymentDeclined(error)) {
    return BoVioErrorCategory.GenericPaymentDeclined
  } else if (getIsGenericFraudDeclined(error)) {
    return BoVioErrorCategory.GenericFraudDeclined
  } else if (getIsGenericCardError(error)) {
    return BoVioErrorCategory.GenericCardError
  } else if (getIsWrongCCError(error)) {
    return BoVioErrorCategory.IncorrectCardNumber
  } else if (getIsWrongCVCError(error)) {
    return BoVioErrorCategory.IncorrectCvc
  } else if (getIsInsufficientFunds(error)) {
    return BoVioErrorCategory.InsufficientFunds
  } else if (getIsCardNotSupported(error)) {
    return BoVioErrorCategory.CardNotSupported
  } else if (getIsPriceMismatch(error)) {
    return BoVioErrorCategory.PriceMismatch
  } else if (getIsDuplicateBooking(error)) {
    return BoVioErrorCategory.DuplicateBooking
  } else if (getIsInvalidEmail(error)) {
    return BoVioErrorCategory.InvalidEmail
  } else if (getIsTokenizationServiceUnavailable(error)) {
    return BoVioErrorCategory.TokenizationServiceUnavailable
  } else if (getIsPaymentLimitExceeded(error)) {
    return BoVioErrorCategory.PaymentLimitExceeded
  } else if (getIsTechnicalIssue(error)) {
    return BoVioErrorCategory.TechnicalError
  } else if (getIsMisusedCard(error)) {
    return BoVioErrorCategory.MisusedCard
  } else if (getIsSecurityBlocks(error)) {
    return BoVioErrorCategory.SecurityBlocks
  } else if (getIsGenericSupplierOrBackendError(error)) {
    return BoVioErrorCategory.Other
  } else if (getIsSoldOutError(error)) {
    return BoVioErrorCategory.SoldOut
  }
  // else if (getIsTaxIdInvalid(error)) {
  //   return BoVioErrorCategory.TaxIdInvalid
  // }
  else if (getIsConnectionError(error)) {
    return NETWORK_ERROR
  }
  return undefined
}

const getIsPsd2AuthError = (
  error: BookingError | Psd2AuthStatusErrorType
): error is Psd2AuthStatusErrorType => {
  return 'errorDetails' in error
}

/**
 * Retrieves the appropriate error message and description for recoverable booking errors
 * based on the provided booking error object.
 * @param error - The booking error object containing details about the issue encountered.
 *                This object should have a `data.details.category` property to determine the error type.
 * @param isPayAtProperty - Optional boolean flag to determine if payment is at property, affects error message selection.
 * @returns An object containing `title` and `description` properties for the recoverable error,
 *          or `null` if the error category is not recoverable.
 * @example
 * ```typescript
 * const error = {
 *   data: {
 *     details: {
 *       category: BoVioErrorCategory.CardNotSupported
 *     }
 *   }
 * };
 * const result = getRecoverableErrorMessages(error);
 * // Expected output:
 * // {
 * //   title: { id: 'errors.cardNotSupportedTitle', defaultMessage: 'Card not supported' },
 * //   description: { id: 'errors.cardNotSupportedDescription', defaultMessage: 'Choose another card type and try again' }
 * // }
 * ```
 */
export const getRecoverableErrorMessages = (
  error: BookingError | Psd2AuthStatusErrorType,
  isPayAtProperty?: boolean
) => {
  let bookingErrorCategory

  const genericPaymentDeclinedErrorTitle = isPayAtProperty
    ? bookingErrorMessages.genericFraudDeclinedTitlePayLater
    : bookingErrorMessages.paymentDeclinedMessage

  if (getIsPsd2AuthError(error)) {
    if (error?.errorDetails?.type === Psd2AuthErrorTypes.CARD_ERROR) {
      bookingErrorCategory = Psd2AuthErrorTypes.CARD_ERROR
    } else if (error.errorType === Psd2AuthErrorTypes.RECOVERABLE) {
      bookingErrorCategory = Psd2AuthErrorTypes.RECOVERABLE
    }
  } else {
    bookingErrorCategory = getMappedBookingErrorToErrorCategory(error)
  }

  if (bookingErrorCategory === BoVioErrorCategory.CardNotSupported) {
    return {
      title: bookingErrorMessages.cardNotSupportedTitle,
      description: bookingErrorMessages.cardNotSupportedDescription
    }
  }
  if (bookingErrorCategory === BoVioErrorCategory.InsufficientFunds) {
    return {
      title: bookingErrorMessages.insufficientFundsTitle,
      description: bookingErrorMessages.genericFraudDeclinedMessageV3
    }
  }
  if (bookingErrorCategory === BoVioErrorCategory.IncorrectCvc) {
    return {
      title: bookingErrorMessages.incorrectCVCTitle,
      description: bookingErrorMessages.incorrectCVCDescription
    }
  }
  if (bookingErrorCategory === BoVioErrorCategory.IncorrectCardNumber) {
    return {
      title: bookingErrorMessages.incorrectCCTitle,
      description: bookingErrorMessages.incorrectCCDescription
    }
  }
  if (bookingErrorCategory === BoVioErrorCategory.InvalidEmail) {
    return {
      title: bookingErrorMessages.invalidEmailTitle,
      description: bookingErrorMessages.invalidEmailDescriptionV3
    }
  }
  if (
    bookingErrorCategory === BoVioErrorCategory.GenericCardError ||
    bookingErrorCategory === Psd2AuthErrorTypes.CARD_ERROR
  ) {
    return {
      title: bookingErrorMessages.genericCardErrorTitle,
      description: bookingErrorMessages.genericCardErrorDescription
    }
  }

  if (bookingErrorCategory === Psd2AuthErrorTypes.RECOVERABLE) {
    return {
      title: bookingErrorMessages.genericErrorMessageTitle,
      description: bookingErrorMessages.genericErrorMessage
    }
  }
  if (bookingErrorCategory === BoVioErrorCategory.GenericPaymentDeclined) {
    return {
      title: genericPaymentDeclinedErrorTitle,
      description: bookingErrorMessages.genericFraudDeclinedMessageV3
    }
  }
  if (bookingErrorCategory === BoVioErrorCategory.Other) {
    return {
      title: bookingErrorMessages.genericErrorMessageTitle,
      description: bookingErrorMessages.contactSupportMessage
    }
  }
  if (
    bookingErrorCategory === BoVioErrorCategory.TokenizationServiceUnavailable
  ) {
    return {
      title: bookingErrorMessages.tokenizationServiceUnavailableTitle,
      description:
        bookingErrorMessages.tokenizationServiceUnavailableDescription
    }
  }
  if (bookingErrorCategory === undefined) {
    return {
      title: bookingErrorMessages.genericErrorMessageTitle,
      description: bookingErrorMessages.genericErrorMessage
    }
  }
  return null
}
